import React, { useEffect, useState } from "react";
import Spinner from "../../components/Spinner";
import Form from "react-bootstrap/Form";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import { AddMetricsReq, Metrics } from "patients-experience-client";
import { Modal } from "react-bootstrap";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { MatrixConstants } from "../PatientProfile/Matrix";

interface AddMetricsProps {
  showModal: boolean;
  handleModal: () => void;
  patientId: string | null;
  refreshMetrics: () => void;
}

export const AddMetrics: React.FC<AddMetricsProps> = ({ showModal, handleModal, patientId, refreshMetrics }) => {
  const patientApi = createPatientApi();
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const [loading, setloading] = useState(false);
  const [heighlightWeight, setHeighlightWeight] = useState<boolean>(false);
  const [heighlightBP, setHeighlightBP] = useState<boolean>(false);
  const [addform, setAddForm] = useState({
    weight: '',
    height: '',
    bmi: '',
    heartRate: '',
    a1c: '',
    bloodSugar: '',
    bloodPressureSys: '',
    bloodPressureDia: '',
    oxygenSaturation: '',
    fev1Actual: '',
    peakFlow: '',
    peakFlowPredicted: '',
    fev1Predicted: '',
    stepCount: ''
  });
  const [isFormResetting, setIsFormResetting] = useState(false);

  const formChnageHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (/^\d*$/.test(value)) {
      setAddForm(addform => ({
        ...addform,
        [name]: value
      }));
    }

    if (/^\d*\.?\d*$/.test(value) && (name === 'a1c' || name === 'weight' || name === 'oxygenSaturation' || name === 'height')) {
      setAddForm(addform => ({
        ...addform,
        [name]: value
      }));
    }
  };

  useEffect(() => {
    const { weight, height } = addform;
    const { bloodPressureSys, bloodPressureDia } = addform;

    if (isFormResetting) {
      setIsFormResetting(false);
      return;
    }

    if ((weight === '' && height !== '') || (weight !== '' && height === '')) {
      setHeighlightWeight(true);
      setAddForm(addform => ({
        ...addform,
        bmi: ''
      }));
    } else {
      setHeighlightWeight(false);
    }

    if (weight !== '' && height !== '') {
      if(Number(weight) === 0 || Number(height) === 0) {
        setHeighlightWeight(true);
        setAddForm(addform => ({
          ...addform,
          bmi: ''
        }));
        return;
      }
      setAddForm(addform => ({
        ...addform,
        bmi: (((Number(weight) / (Number(height) * Number(height))) * 703).toFixed(2)).toString()
      }));
    }

    if ((bloodPressureSys === '' && bloodPressureDia !== '') || (bloodPressureSys !== '' && bloodPressureDia === '')) {
      setHeighlightBP(true);
    } else {
      setHeighlightBP(false);
    }
  }, [addform]);

  const resetForm = () => {
    setIsFormResetting(true);
    setAddForm({
      weight: '',
      height: '',
      bmi: '',
      heartRate: '',
      a1c: '',
      bloodSugar: '',
      bloodPressureSys: '',
      bloodPressureDia: '',
      oxygenSaturation: '',
      fev1Actual: '',
      peakFlow: '',
      peakFlowPredicted: '',
      fev1Predicted: '',
      stepCount: ''
    });
    setHeighlightBP(false);
    setHeighlightWeight(false);
  }

  useEffect(() => {
    if(isFormResetting) {
      resetForm();
      setIsFormResetting(false);
    }
  }, [isFormResetting]);

  const submitHandler = async (event: any) => {
    event.preventDefault();

    if (heighlightBP || heighlightWeight) {
      return;
    }

    const data: Metrics[] = [
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.WEIGHT,
        metricValue: Number(addform.weight),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.HEIGHT,
        metricValue: Number(addform.height),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.PULSE,
        metricValue: Number(addform.heartRate),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.A1C,
        metricValue: Number(addform.a1c),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.BLOOD_SUGAR,
        metricValue: Number(addform.bloodSugar),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.BLOOD_PRESSURE_SYSTOLIC,
        metricValue: Number(addform.bloodPressureSys),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.BLOOD_PRESSURE_DIASTOLIC,
        metricValue: Number(addform.bloodPressureDia),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.O2_READING,
        metricValue: Number(addform.oxygenSaturation),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.FEV1_ACTUAL,
        metricValue: Number(addform.fev1Actual),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.PEAK_FLOW_ACTUAL,
        metricValue: Number(addform.peakFlow),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.PEAK_FLOW_PREDICTED,
        metricValue: Number(addform.peakFlowPredicted),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.FEV1_PREDICTED,
        metricValue: Number(addform.fev1Predicted),
      },
      {
        measuredAt: new Date(),
        source: "During Appointment",
        metricType: MatrixConstants.STEPS_COUNT,
        metricValue: Number(addform.stepCount),
      },
    ];

    const filteredData = data.filter((d) => d.metricValue !== 0);

    const req: AddMetricsReq = {
      metricData: filteredData,
    };

    setloading(true);
    try {
      await patientApi.patientsControllerSavePatientMetrics(req, patientId!);
      const toasterConfig: ToastConfig = {
        type: "success",
        title: 'Metric added successfully!',
        icon: "check_circle_outline",
      };
      setToastConfig(toasterConfig);
      setShowToast(true);
      handleModal();
      resetForm();
      refreshMetrics();
    } catch (ex: any) {
      const toasterConfig: ToastConfig = {
        type: "danger",
        title: "Error while adding metric!",
        icon: "error_outline",
      };
      setToastConfig(toasterConfig);
      setShowToast(true);
    } finally {
      setloading(false);
    }
  }

  return (
    <>
      <Toaster
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        title={toastConfig.title}
        message={toastConfig.message}
        icon={toastConfig.icon}
      />
      <Modal show={showModal} onHide={handleModal} centered
        backdrop="static" // Prevents closing on outside click
        keyboard={false} // Prevents closing on pressing the "Escape" key
      >
        <Form
          className="d-flex flex-column h-100"
          onSubmit={submitHandler}
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Metrics</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loading && <Spinner />}

            <div className="flex-fill">
              <div className="row g-2">
                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Weight
                  </label>
                  <div className="input-group">
                    <input placeholder="Weight" type="text" onChange={formChnageHandler} name="weight" className={`form-control form-control-sm ${heighlightWeight ? 'border-danger' : ''}`} value={addform.weight} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">lbs</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Height
                  </label>
                  <div className="input-group">
                    <input placeholder="Height" type="text" onChange={formChnageHandler} name="height" className={`form-control form-control-sm ${heighlightWeight ? 'border-danger' : ''}`} value={addform.height} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">inches</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    BMI
                  </label>
                  <div className="input-group">
                    <input placeholder="BMI" disabled type="text" className="form-control form-control-sm" value={addform.bmi} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">lbs/in</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Heart Rate
                  </label>
                  <div className="input-group">
                    <input placeholder="Heart Rate" type="text" onChange={formChnageHandler} name="heartRate" className="form-control form-control-sm" value={addform.heartRate} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">bpm</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    A1C
                  </label>
                  <div className="input-group">
                    <input placeholder="A1C" type="text" onChange={formChnageHandler} name="a1c" className="form-control form-control-sm" value={addform.a1c} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">%</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Blood Sugar
                  </label>
                  <div className="input-group">
                    <input placeholder="Blood Sugar" type="text" onChange={formChnageHandler} name="bloodSugar" className="form-control form-control-sm" value={addform.bloodSugar} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">mg/dl</span>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <h5 className="form-label fs-12 fw-bold mb-0">Pulse Ox</h5>
                  <hr className="mb-2 mt-1" />
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Blood Pressure (SYS)
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="SYS" onChange={formChnageHandler} name="bloodPressureSys" className={`form-control form-control-sm ${heighlightBP ? 'border-danger' : ''}`} value={addform.bloodPressureSys} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">mm/Hg</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Blood Pressure (DIA)
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="DIA" onChange={formChnageHandler} name="bloodPressureDia" className={`form-control form-control-sm ${heighlightBP ? 'border-danger' : ''}`} value={addform.bloodPressureDia} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">mm/Hg</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Oxygen Saturation
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="O2" onChange={formChnageHandler} name="oxygenSaturation" className="form-control form-control-sm" value={addform.oxygenSaturation} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">%</span>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <h5 className="form-label fs-12 fw-bold mb-0">Spirometry</h5>
                  <hr className="mb-2 mt-1" />
                </div>
                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    FEV1 Actual
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="FEV1" onChange={formChnageHandler} name="fev1Actual" className="form-control form-control-sm" value={addform.fev1Actual} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">L</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Peak Flow
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="Peak Flow" onChange={formChnageHandler} name="peakFlow" className="form-control form-control-sm" value={addform.peakFlow} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">L/min</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Peak Flow Predicted
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="Peak Flow Predicted" onChange={formChnageHandler} name="peakFlowPredicted" className="form-control form-control-sm" value={addform.peakFlowPredicted} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">L/min</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4 col-sm-6 col-12 mb-3">
                  <label className="form-label fs-12 fw-semibold mb-1">
                    FEV1 Predicted
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="FEV1 Predicted" onChange={formChnageHandler} name="fev1Predicted" className="form-control form-control-sm" value={addform.fev1Predicted} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">L</span>
                    </div>
                  </div>
                </div>

                <div className="col-12 mb-3">
                  <h5 className="form-label fs-12 fw-bold mb-0">Pedometer</h5>
                  <hr className="mb-2 mt-1" />
                  <label className="form-label fs-12 fw-semibold mb-1">
                    Step Count (Daily)
                  </label>
                  <div className="input-group">
                    <input type="text" placeholder="Step Count" onChange={formChnageHandler} name="stepCount" className="form-control form-control-sm" value={addform.stepCount} />
                    <div className="input-group-append">
                      <span className="input-group-text metric-unit">steps</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="w-100 d-flex justify-content-between">
              <div>
                <button
                  className="btn btn-outline-primary rounded-pill px-5"      
                  type="reset" onClick={() => {
                    resetForm();
                    handleModal();
                  }}>
                  Discard
                </button>
              </div>
              <div>
                <button
                  className="btn btn-primary rounded-pill px-5"
                  type="submit" disabled={Object.values(addform).every(value => value === '') || heighlightBP || heighlightWeight}
                >
                  Submit
                </button>
              </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};
